import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ZoomIn, ZoomOut, RotateCw, Maximize, Minimize, X, ChevronLeft, ChevronRight } from 'lucide-react';
import '../AllCss/VirtualTourViewer.css';

function VirtualTourViewer({ images, views, onClose, carName }) {
  // Support multiple views: views = { exterior: [...], interior: [...], engine: [...] }
  // If views is provided, use it; otherwise fall back to images array
  const viewTypes = views ? Object.keys(views) : [];
  const [currentView, setCurrentView] = useState(viewTypes[0] || 'default');
  const currentImages = useMemo(
    () => (views ? (views[currentView] || []) : (images || [])),
    [views, currentView, images]
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState([]);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const autoRotateIntervalRef = useRef(null);

  // Preload all images
  useEffect(() => {
    if (!currentImages || currentImages.length === 0) {
      setImagesLoaded(false);
      setLoadedImages([]);
      return;
    }

    const imagePromises = currentImages.map((src, index) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve({ img, index });
        img.onerror = reject;
        img.src = src;
      });
    });

    Promise.all(imagePromises)
      .then((results) => {
        const sorted = results.sort((a, b) => a.index - b.index);
        setLoadedImages(sorted.map(r => r.img));
        setImagesLoaded(true);
      })
      .catch((err) => {
        console.error('Error loading 360° images:', err);
        setImagesLoaded(true); // Still allow viewing even if some fail
      });
  }, [currentImages]);

  // Reset to first image when view changes
  useEffect(() => {
    setCurrentIndex(0);
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, [currentView]);

  // Auto-rotate functionality
  useEffect(() => {
    if (autoRotate && imagesLoaded && loadedImages.length > 0) {
      autoRotateIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % loadedImages.length);
      }, 100); // Rotate every 100ms for smooth animation
    } else {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
        autoRotateIntervalRef.current = null;
      }
    }

    return () => {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
      }
    };
  }, [autoRotate, imagesLoaded, loadedImages.length]);

  // Draw image on canvas
  useEffect(() => {
    if (!canvasRef.current || !imagesLoaded || loadedImages.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = loadedImages[currentIndex];

    if (!img) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate scaled dimensions based on zoom
    const scale = zoom;
    const imgWidth = img.width * scale;
    const imgHeight = img.height * scale;

    // Calculate position with pan
    const x = (canvas.width - imgWidth) / 2 + panX;
    const y = (canvas.height - imgHeight) / 2 + panY;

    // Draw image
    ctx.drawImage(img, x, y, imgWidth, imgHeight);
  }, [currentIndex, zoom, panX, panY, imagesLoaded, loadedImages]);

  // Handle mouse drag for rotation
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setAutoRotate(false); // Stop auto-rotate when user interacts
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !imagesLoaded || loadedImages.length === 0) return;

    const deltaX = e.clientX - startX;
    const sensitivity = 0.5; // Adjust rotation sensitivity
    const indexChange = Math.round(deltaX / (window.innerWidth / loadedImages.length) * sensitivity);

    if (indexChange !== 0) {
      setCurrentIndex((prev) => {
        const newIndex = prev + indexChange;
        if (newIndex < 0) return loadedImages.length - 1;
        if (newIndex >= loadedImages.length) return 0;
        return newIndex;
      });
      setStartX(e.clientX);
    }
  }, [isDragging, startX, imagesLoaded, loadedImages.length]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle touch events for mobile
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      setAutoRotate(false);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !imagesLoaded || loadedImages.length === 0) return;
    e.preventDefault();

    if (e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - startX;
      const sensitivity = 0.5;
      const indexChange = Math.round(deltaX / (window.innerWidth / loadedImages.length) * sensitivity);

      if (indexChange !== 0) {
        setCurrentIndex((prev) => {
          const newIndex = prev + indexChange;
          if (newIndex < 0) return loadedImages.length - 1;
          if (newIndex >= loadedImages.length) return 0;
          return newIndex;
        });
        setStartX(e.touches[0].clientX);
      }
    }
  }, [isDragging, startX, imagesLoaded, loadedImages.length]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle zoom with mouse wheel
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.max(0.5, Math.min(3, prev + delta)));
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!imagesLoaded || loadedImages.length === 0) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setCurrentIndex((prev) => (prev - 1 + loadedImages.length) % loadedImages.length);
          setAutoRotate(false);
          break;
        case 'ArrowRight':
          e.preventDefault();
          setCurrentIndex((prev) => (prev + 1) % loadedImages.length);
          setAutoRotate(false);
          break;
        case '+':
        case '=':
          e.preventDefault();
          setZoom((prev) => Math.min(3, prev + 0.2));
          break;
        case '-':
        case '_':
          e.preventDefault();
          setZoom((prev) => Math.max(0.5, prev - 0.2));
          break;
        case '0':
          e.preventDefault();
          setZoom(1);
          setPanX(0);
          setPanY(0);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case ' ':
          e.preventDefault();
          setAutoRotate((prev) => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imagesLoaded, loadedImages.length, onClose]);

  // Zoom controls
  const handleZoomIn = () => setZoom((prev) => Math.min(3, prev + 0.2));
  const handleZoomOut = () => setZoom((prev) => Math.max(0.5, prev - 0.2));
  const handleResetZoom = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };

  // Fullscreen functionality
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!currentImages || currentImages.length === 0) {
    return (
      <div className="virtual-tour-viewer">
        <div className="virtual-tour-header">
          <h3>360° Virtual Tour - {carName}</h3>
          <button className="virtual-tour-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="virtual-tour-error">
          <p>No 360° images available for this vehicle.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="virtual-tour-viewer" ref={containerRef}>
      <div className="virtual-tour-header">
        <div className="virtual-tour-header-left">
          <h3>360° Virtual Tour - {carName}</h3>
          {viewTypes.length > 1 && (
            <div className="virtual-tour-view-selector">
              {viewTypes.map((viewType) => (
                <button
                  key={viewType}
                  className={`virtual-tour-view-btn ${currentView === viewType ? 'active' : ''}`}
                  onClick={() => setCurrentView(viewType)}
                >
                  {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="virtual-tour-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
      </div>

      <div className="virtual-tour-canvas-container">
        {!imagesLoaded && (
          <div className="virtual-tour-loading">
            <div className="spinner"></div>
            <p>Loading 360° images...</p>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="virtual-tour-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        />

        {imagesLoaded && (
          <div className="virtual-tour-instructions">
            <p>Drag to rotate • Scroll to zoom • Arrow keys to navigate • Space to auto-rotate</p>
          </div>
        )}

        {/* Navigation arrows */}
        {imagesLoaded && loadedImages.length > 1 && (
          <>
            <button
              className="virtual-tour-nav-arrow virtual-tour-nav-left"
              onClick={() => {
                setCurrentIndex((prev) => (prev - 1 + loadedImages.length) % loadedImages.length);
                setAutoRotate(false);
              }}
              aria-label="Previous frame"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              className="virtual-tour-nav-arrow virtual-tour-nav-right"
              onClick={() => {
                setCurrentIndex((prev) => (prev + 1) % loadedImages.length);
                setAutoRotate(false);
              }}
              aria-label="Next frame"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      <div className="virtual-tour-controls">
        <div className="virtual-tour-control-group">
          <button
            className="virtual-tour-control-btn"
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
            title="Zoom Out"
          >
            <ZoomOut size={18} />
          </button>
          <span className="virtual-tour-zoom-level">{Math.round(zoom * 100)}%</span>
          <button
            className="virtual-tour-control-btn"
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            title="Zoom In"
          >
            <ZoomIn size={18} />
          </button>
          <button
            className="virtual-tour-control-btn"
            onClick={handleResetZoom}
            title="Reset Zoom"
          >
            Reset
          </button>
        </div>

        <div className="virtual-tour-control-group">
          <button
            className={`virtual-tour-control-btn ${autoRotate ? 'active' : ''}`}
            onClick={() => setAutoRotate(!autoRotate)}
            title="Auto Rotate"
          >
            <RotateCw size={18} />
          </button>
          <button
            className="virtual-tour-control-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>
      </div>

      {imagesLoaded && loadedImages.length > 0 && (
        <>
          <div className="virtual-tour-progress">
            <div className="virtual-tour-progress-bar">
              <div
                className="virtual-tour-progress-fill"
                style={{ width: `${((currentIndex + 1) / loadedImages.length) * 100}%` }}
              />
            </div>
            <span className="virtual-tour-progress-text">
              {currentIndex + 1} / {loadedImages.length}
            </span>
          </div>

          {/* Thumbnail strip */}
          {showThumbnails && loadedImages.length > 1 && (
            <div className="virtual-tour-thumbnails">
              <button
                className="virtual-tour-thumbnail-toggle"
                onClick={() => setShowThumbnails(false)}
                title="Hide thumbnails"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="virtual-tour-thumbnail-strip">
                {loadedImages.map((img, index) => (
                  <button
                    key={index}
                    className={`virtual-tour-thumbnail ${currentIndex === index ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentIndex(index);
                      setAutoRotate(false);
                    }}
                    title={`Frame ${index + 1}`}
                  >
                    <img src={img.src} alt={`Frame ${index + 1}`} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!showThumbnails && loadedImages.length > 1 && (
            <button
              className="virtual-tour-thumbnail-toggle virtual-tour-thumbnail-toggle-show"
              onClick={() => setShowThumbnails(true)}
              title="Show thumbnails"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default VirtualTourViewer;

